import axios from 'axios';
import { WordPressPost, Category, Tag } from '../types/wordpress';

// API base URL - Auto-detect current domain
const getApiBaseUrl = (): string => {
  // Nếu có environment variable, sử dụng nó
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Tự động detect domain hiện tại
  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;
    
    // Nếu đang ở localhost development mode
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5000/api`;
    }
    
    // Production mode - sử dụng domain hiện tại
    return `${protocol}//${hostname}${port ? `:${port}` : ''}/api`;
  }
  
  // Fallback cho server-side rendering
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

console.log('API Base URL:', API_BASE_URL);

// Create axios instance with timeout and retry
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10s timeout
});

// Utility function to parse image URLs from post content (extracted duplicate logic)
const parseImageUrls = (post: WordPressPost): string[] => {
  try {
    let imageUrls: string[] = Array.isArray(post.imageUrls) ? post.imageUrls.filter(Boolean) : [];
    
    // Parse lại tất cả <img> trong content.rendered
    if (post.content && post.content.rendered) {
      const html = post.content.rendered;
      const imgRegex = /<img[^>]+src=["']([^"'>]+)["'][^>]*>/g;
      let match;
      while ((match = imgRegex.exec(html)) !== null) {
        if (match[1] && !imageUrls.includes(match[1])) {
          imageUrls.push(match[1]);
        }
      }
    }
    
    // Ưu tiên ảnh lớn: sort theo tên chứa 'large', 'gallery', 'img', 'thumb'
    imageUrls = imageUrls.sort((a, b) => {
      const score = (url: string) =>
        (url.includes('large') ? 2 : 0) +
        (url.includes('gallery') ? 2 : 0) +
        (url.includes('img') ? 1 : 0) -
        (url.includes('thumb') ? 2 : 0);
      return score(b) - score(a);
    });
    
    return imageUrls.filter(Boolean);
  } catch (error) {
    console.error('Error parsing image URLs:', error);
    return []; // Fallback to empty array
  }
};

// Utility function to parse audio URL from post content
const parseAudioUrl = (post: WordPressPost): string | undefined => {
  try {
    if (!post.content || !post.content.rendered) {
      return undefined;
    }
    
    const html = post.content.rendered;
    
    // Parse audio từ <audio> tags
    const audioRegex = /<audio[^>]*src=["']([^"']*\.(?:mp3|wav|ogg|m4a))["'][^>]*>/i;
    const audioMatch = html.match(audioRegex);
    if (audioMatch && audioMatch[1]) {
      console.log('🎵 Audio URL found from <audio> tag:', audioMatch[1]);
      return audioMatch[1];
    }
    
    // Parse audio từ links
    const linkRegex = /<a[^>]*href=["']([^"']*\.(?:mp3|wav|ogg|m4a))["'][^>]*>/i;
    const linkMatch = html.match(linkRegex);
    if (linkMatch && linkMatch[1]) {
      console.log('🎵 Audio URL found from link:', linkMatch[1]);
      return linkMatch[1];
    }
    
    // Parse audio từ paragraph URLs
    const pRegex = /<p[^>]*>\s*(https?:\/\/[^\s<>"]*\.(?:mp3|wav|ogg|m4a))\s*<\/p>/i;
    const pMatch = html.match(pRegex);
    if (pMatch && pMatch[1]) {
      console.log('🎵 Audio URL found from paragraph:', pMatch[1]);
      return pMatch[1];
    }
    
    return undefined;
  } catch (error) {
    console.error('Error parsing audio URL:', error);
    return undefined;
  }
};

// Utility function to parse subtitle URL from post content
const parseSubtitleUrl = (post: WordPressPost): string | undefined => {
  try {
    if (!post.content || !post.content.rendered) {
      return undefined;
    }
    
    const html = post.content.rendered;
    
    // Parse subtitle từ links (.srt, .vtt)
    const srtRegex = /<a[^>]*href=["']([^"']*\.(?:srt|vtt))["'][^>]*>/i;
    const srtMatch = html.match(srtRegex);
    if (srtMatch && srtMatch[1]) {
      console.log('📝 Subtitle URL found:', srtMatch[1]);
      return srtMatch[1];
    }
    
    // Parse subtitle từ paragraph URLs
    const pRegex = /<p[^>]*>\s*(https?:\/\/[^\s<>"]*\.(?:srt|vtt))\s*<\/p>/i;
    const pMatch = html.match(pRegex);
    if (pMatch && pMatch[1]) {
      console.log('📝 Subtitle URL found from paragraph:', pMatch[1]);
      return pMatch[1];
    }
    
    return undefined;
  } catch (error) {
    console.error('Error parsing subtitle URL:', error);
    return undefined;
  }
};

// Utility function to retry requests (extracted duplicate logic)
const withRetry = async <T>(fn: () => Promise<T>, retries = 2): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0) {
      console.warn(`Request failed, retrying... (${retries} attempts left)`);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1s before retry
      return withRetry(fn, retries - 1);
    }
    throw error;
  }
};

// Fetch categories from WordPress via proxy
export const fetchCategories = async (page = 1, perPage = 50): Promise<Category[]> => {
  try {
    const response = await withRetry(() => api.get('/categories', {
      params: {
        page,
        per_page: perPage,
      },
    }));
    
    if (!response.data || !Array.isArray(response.data)) {
      console.error('Invalid response format from /categories');
      return [];
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching categories:', error);
    return []; // Fallback to empty array instead of throwing
  }
};

// Fetch tags from WordPress via proxy
export const fetchTags = async (page = 1, perPage = 50): Promise<Tag[]> => {
  try {
    const response = await withRetry(() => api.get('/tags', {
      params: {
        page,
        per_page: perPage,
      },
    }));
    
    if (!response.data || !Array.isArray(response.data)) {
      console.error('Invalid response format from /tags');
      return [];
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching tags:', error);
    return []; // Fallback to empty array instead of throwing
  }
};

// Utility function to generate shared cache key based on time intervals
const generateSharedCacheKey = (intervalMinutes: number = 1): string => {
  // Create predictable cache key that changes every N minutes
  // All users within the same interval will share the same cache key
  const now = Date.now();
  const intervalMs = intervalMinutes * 60 * 1000; // Convert minutes to milliseconds
  const intervalKey = Math.floor(now / intervalMs);
  
  // Format: cache_YYYYMMDD_HHMM_interval
  const date = new Date(intervalKey * intervalMs);
  const dateStr = date.toISOString().slice(0, 16).replace(/[-:T]/g, ''); // YYYYMMDDHHMM
  
  return `cache_${dateStr}_${intervalMinutes}m`;
};

// Enhanced cache bypass strategy with shared keys
const getOptimalCacheKey = (forceRefresh: boolean = false): string | undefined => {
  if (!forceRefresh) {
    return undefined; // Use default cache
  }
  
  // Use 1-minute intervals for normal refresh (max 60 unique keys per hour)
  // This means all users within the same minute share the same cache key
  const sharedKey = generateSharedCacheKey(1);
  
  console.log(`[API] 🔑 Generated shared cache key: ${sharedKey}`);
  return sharedKey;
};

// Alternative: 5-minute intervals for even better resource optimization
const getConservativeCacheKey = (forceRefresh: boolean = false): string | undefined => {
  if (!forceRefresh) {
    return undefined;
  }
  
  // Use 5-minute intervals (max 12 unique keys per hour)
  // Even better for high-traffic scenarios
  const sharedKey = generateSharedCacheKey(5);
  
  console.log(`[API] 🔑 Generated conservative cache key: ${sharedKey} (5min interval)`);
  return sharedKey;
};

// Fetch posts from WordPress via proxy (OPTIMIZED: Always fresh with shared cache keys)
export const fetchPosts = async (
  page = 1, 
  perPage = 10, 
  categories?: number[], 
  tags?: number[],
  forceNoCache?: boolean, // NEW: Option to disable shared cache for special cases
  languageCategoryId?: string // NEW: Language category ID for filtering
): Promise<WordPressPost[]> => {
  try {
    const params: any = {
      page,
      per_page: perPage,
      _embed: 'wp:term', // Enable embedded tags for optimization
    };
    
    if (categories && categories.length > 0) {
      params.categories = categories.join(',');
    }
    
    if (tags && tags.length > 0) {
      params.tags = tags.join(',');
    }
    
    // NEW: Add language category filtering
    if (languageCategoryId) {
      const languageCategoryNum = parseInt(languageCategoryId);
      if (!isNaN(languageCategoryNum)) {
        // Merge language category with existing categories
        const allCategories = categories ? [...categories, languageCategoryNum] : [languageCategoryNum];
        params.categories = allCategories.join(',');
        console.log(`[fetchPosts] 🌍 Language filtering enabled: category ${languageCategoryId}`);
      }
    }
    
    // 🚀 NEW OPTIMIZATION: Always use shared cache key for fresh content
    // This ensures users ALWAYS get fresh content while maintaining minimal backend load
    if (!forceNoCache) {
      const sharedCacheKey = getOptimalCacheKey(true);
      if (sharedCacheKey) {
        params._cacheKey = sharedCacheKey;
        console.log(`[fetchPosts] 🌟 ALWAYS FRESH: Using shared cache key: ${sharedCacheKey}`);
      }
    } else {
      console.log(`[fetchPosts] 🔄 NO CACHE: Forced fresh request (special case)`);
    }
    
    console.log(`[fetchPosts] 🎯 Fetching posts with embedded tags: page ${page}, perPage ${perPage}${!forceNoCache ? ` (ALWAYS FRESH: ${params._cacheKey})` : ' (NO CACHE)'}`);
    const response = await withRetry(() => api.get('/posts', { params }));
    
    if (!response.data || !Array.isArray(response.data)) {
      console.error('Invalid response format from /posts');
      return [];
    }
    
    return response.data.map((post: WordPressPost) => {
      // Check if tags are embedded (optimized path)
      const hasEmbeddedTags = response.headers['x-tags-embedded'] === 'true';
      let tagDetails: Array<{id: number; name: string; slug: string; count: number}> = [];
      
      if (hasEmbeddedTags && post.tagDetails) {
        console.log(`[fetchPosts] ✅ Using embedded tags for post ${post.id}: ${post.tagDetails.length} tags`);
        tagDetails = post.tagDetails;
      } else {
        console.log(`[fetchPosts] ⚠️ No embedded tags for post ${post.id}, will use legacy tagIds: ${post.tags?.length || 0} tags`);
      }
      
      return {
        ...post,
        tagDetails, // Add embedded tag details for optimization
        imageUrls: parseImageUrls(post),
        audioUrl: parseAudioUrl(post),
        subtitleUrl: parseSubtitleUrl(post)
      };
    });
  } catch (error) {
    console.error('Error fetching posts:', error);
    return []; // Fallback to empty array instead of throwing
  }
};

// Legacy function for backwards compatibility (now just calls main fetchPosts)
export const fetchFreshPosts = async (
  page = 1, 
  perPage = 10, 
  categories?: number[], 
  tags?: number[],
  languageCategoryId?: string
): Promise<WordPressPost[]> => {
  console.log(`[fetchFreshPosts] 📢 DEPRECATED: fetchPosts now always returns fresh content by default`);
  return fetchPosts(page, perPage, categories, tags, false, languageCategoryId); // No need for special handling
};

// Conservative version with 5-minute intervals for extremely high traffic
export const fetchPostsConservative = async (
  page = 1, 
  perPage = 10, 
  categories?: number[], 
  tags?: number[],
  languageCategoryId?: string
): Promise<WordPressPost[]> => {
  try {
    const params: any = {
      page,
      per_page: perPage,
      _embed: 'wp:term',
    };
    
    if (categories && categories.length > 0) {
      params.categories = categories.join(',');
    }
    
    if (tags && tags.length > 0) {
      params.tags = tags.join(',');
    }
    
    // NEW: Add language category filtering
    if (languageCategoryId) {
      const languageCategoryNum = parseInt(languageCategoryId);
      if (!isNaN(languageCategoryNum)) {
        // Merge language category with existing categories
        const allCategories = categories ? [...categories, languageCategoryNum] : [languageCategoryNum];
        params.categories = allCategories.join(',');
        console.log(`[fetchPostsConservative] 🌍 Language filtering enabled: category ${languageCategoryId}`);
      }
    }
    
    // Use 5-minute intervals for better resource optimization
    const conservativeCacheKey = getConservativeCacheKey(true);
    if (conservativeCacheKey) {
      params._cacheKey = conservativeCacheKey;
    }
    
    console.log(`[fetchFreshPostsConservative] 🛡️ Conservative fetch with 5min cache key: ${conservativeCacheKey}`);
    const response = await withRetry(() => api.get('/posts', { params }));
    
    if (!response.data || !Array.isArray(response.data)) {
      console.error('Invalid response format from /posts');
      return [];
    }
    
    return response.data.map((post: any) => {
      return {
        ...post,
        tagDetails: post.tagDetails || [],
        imageUrls: parseImageUrls(post),
        audioUrl: parseAudioUrl(post),
        subtitleUrl: parseSubtitleUrl(post)
      };
    });
  } catch (error) {
    console.error('Error fetching posts (conservative):', error);
    return [];
  }
};

// Special function for cases where absolutely no cache is desired
export const fetchPostsNoCache = async (
  page = 1, 
  perPage = 10, 
  categories?: number[], 
  tags?: number[],
  languageCategoryId?: string
): Promise<WordPressPost[]> => {
  console.log(`[fetchPostsNoCache] 🚫 Force no cache request (admin/debug use only)`);
  return fetchPosts(page, perPage, categories, tags, true, languageCategoryId); // Force no cache
};

// NEW: Smart refresh logic with resource-optimized cache keys
export const checkForNewContent = async (
  latestPostDate: string,
  perPage = 10,
  languageCategoryId?: string
): Promise<{
  hasNewContent: boolean;
  newPosts: WordPressPost[];
  totalNewPosts: number;
}> => {
  try {
    console.log(`[checkForNewContent] 🔍 Checking for new content since: ${latestPostDate}`);
    
    // Use fetchPostsConservative for better resource optimization
    const freshPosts = await fetchPostsConservative(1, perPage, undefined, undefined, languageCategoryId);
    
    if (freshPosts.length === 0) {
      return {
        hasNewContent: false,
        newPosts: [],
        totalNewPosts: 0
      };
    }
    
    // Compare timestamps - find posts newer than latestPostDate
    const latestTimestamp = new Date(latestPostDate).getTime();
    const newPosts = freshPosts.filter(post => {
      const postTimestamp = new Date(post.date || '').getTime();
      return postTimestamp > latestTimestamp;
    });
    
    console.log(`[checkForNewContent] 📊 Results:`, {
      totalFreshPosts: freshPosts.length,
      newPostsFound: newPosts.length,
      latestPostDate,
      hasNewContent: newPosts.length > 0
    });
    
    return {
      hasNewContent: newPosts.length > 0,
      newPosts,
      totalNewPosts: newPosts.length
    };
    
  } catch (error) {
    console.error('[checkForNewContent] ❌ Error checking for new content:', error);
    return {
      hasNewContent: false,
      newPosts: [],
      totalNewPosts: 0
    };
  }
};

// NEW: Merge new posts with existing posts, avoiding duplicates
export const mergeNewPosts = (
  existingPosts: WordPressPost[],
  newPosts: WordPressPost[]
): {
  mergedPosts: WordPressPost[];
  addedCount: number;
} => {
  if (newPosts.length === 0) {
    return {
      mergedPosts: existingPosts,
      addedCount: 0
    };
  }
  
  const existingIds = new Set(existingPosts.map(p => p.id));
  const uniqueNewPosts = newPosts.filter(p => !existingIds.has(p.id));
  
  console.log(`[mergeNewPosts] 🔄 Merging posts:`, {
    existingCount: existingPosts.length,
    newPostsCount: newPosts.length,
    uniqueNewCount: uniqueNewPosts.length,
    duplicatesFiltered: newPosts.length - uniqueNewPosts.length
  });
  
  // ✅ FIXED: Add new posts to the end of the array (inject below current content)
  const mergedPosts = [...existingPosts, ...uniqueNewPosts];
  
  return {
    mergedPosts,
    addedCount: uniqueNewPosts.length
  };
};

// Fetch a single post by ID (OPTIMIZED with embedded tags)
export const fetchPostById = async (id: number): Promise<WordPressPost | null> => {
  try {
    if (!id || id <= 0) {
      console.error('Invalid post ID:', id);
      return null;
    }
    
    console.log(`[fetchPostById] 🎯 Fetching single post with embedded tags: ${id}`);
    const response = await withRetry(() => api.get(`/posts/${id}`));
    
    if (!response.data) {
      console.error('No data received for post ID:', id);
      return null;
    }

    const post = response.data;
    
    // Check if tags are embedded (optimized path)
    const hasEmbeddedTags = response.headers['x-tags-embedded'] === 'true';
    let tagDetails: Array<{id: number; name: string; slug: string; count: number}> = [];
    
    if (hasEmbeddedTags && post.tagDetails) {
      console.log(`[fetchPostById] ✅ Using embedded tags for post ${id}: ${post.tagDetails.length} tags`);
      tagDetails = post.tagDetails;
    } else {
      console.log(`[fetchPostById] ⚠️ No embedded tags for post ${id}, will use legacy tagIds: ${post.tags?.length || 0} tags`);
    }
    
    return {
      ...post,
      tagDetails, // Add embedded tag details for optimization
      imageUrls: parseImageUrls(post),
      audioUrl: parseAudioUrl(post),
      subtitleUrl: parseSubtitleUrl(post)
    };
  } catch (error: any) {
    console.error(`Error fetching post ${id}:`, error);
    
    // Handle 404 specifically - post not found
    if (error.response && error.response.status === 404) {
      return null; // Post not found, return null
    }
    
    // For other errors, still return null but log the error
    return null;
  }
};

// Fetch a post by slug (OPTIMIZED with embedded tags)
export const fetchPostBySlug = async (slug: string): Promise<WordPressPost | null> => {
  try {
    if (!slug || typeof slug !== 'string') {
      console.error('Invalid slug:', slug);
      return null;
    }
    
    console.log(`[fetchPostBySlug] 🎯 Fetching post by slug with embedded tags: ${slug}`);
    const response = await withRetry(() => api.get(`/posts/by-slug/${slug}`));
    
    if (!response.data) {
      console.error('No data received for slug:', slug);
      return null;
    }

    const post = response.data;
    
    // Check if tags are embedded (optimized path)
    const hasEmbeddedTags = response.headers['x-tags-embedded'] === 'true';
    let tagDetails: Array<{id: number; name: string; slug: string; count: number}> = [];
    
    if (hasEmbeddedTags && post.tagDetails) {
      console.log(`[fetchPostBySlug] ✅ Using embedded tags for slug ${slug}: ${post.tagDetails.length} tags`);
      tagDetails = post.tagDetails;
    } else {
      console.log(`[fetchPostBySlug] ⚠️ No embedded tags for slug ${slug}, will use legacy tagIds: ${post.tags?.length || 0} tags`);
    }
    
    return {
      ...post,
      tagDetails, // Add embedded tag details for optimization
      imageUrls: parseImageUrls(post),
      audioUrl: parseAudioUrl(post),
      subtitleUrl: parseSubtitleUrl(post)
    };
  } catch (error: any) {
    console.error(`Error fetching post with slug ${slug}:`, error);
    
    // Handle 404 specifically - post not found
    if (error.response && error.response.status === 404) {
      return null; // Post not found, return null
    }
    
    // For other errors, still return null but log the error
    return null;
  }
};

// Get media URL through proxy
export const getProxyMediaUrl = (originalUrl: string): string => {
  if (!originalUrl || typeof originalUrl !== 'string') {
    console.warn('Invalid URL provided to getProxyMediaUrl:', originalUrl);
    return '';
  }
  
  try {
    // Encode the URL to base64 as expected by the server
    const encoded = btoa(originalUrl);
    return `${API_BASE_URL}/media/${encoded}`;
  } catch (error) {
    console.error('Error encoding media URL:', error);
    return originalUrl; // Fallback to original URL
  }
};

// Get optimized image URL through new image proxy with size support
export const getProxyImageUrl = (originalUrl: string, size: string = 'medium'): string => {
  if (!originalUrl || typeof originalUrl !== 'string') {
    console.warn('Invalid URL provided to getProxyImageUrl:', originalUrl);
    return '';
  }
  
  try {
    // Encode the URL to base64 as expected by the server
    const encoded = btoa(originalUrl);
    return `${API_BASE_URL}/images/proxy/${size}/${encoded}`;
  } catch (error) {
    console.error('Error encoding image URL:', error);
    return originalUrl; // Fallback to original URL
  }
};

// Get available image sizes from proxy service
export const getAvailableImageSizes = async (): Promise<{
  availableSizes: string[];
  sizeConfigurations: Record<string, any>;
  defaultSize: string;
} | null> => {
  try {
    const response = await withRetry(() => api.get('/images/sizes'));
    return response.data;
  } catch (error) {
    console.error('Error fetching available image sizes:', error);
    return null;
  }
};

// Parse SRT subtitle format
export const parseSRT = (srtContent: string): Array<{start: number, end: number, text: string}> => {
  if (!srtContent || typeof srtContent !== 'string') {
    console.warn('Invalid SRT content:', srtContent);
    return [];
  }
  
  try {
    const subtitles: Array<{start: number, end: number, text: string}> = [];
    const blocks = srtContent.trim().split('\n\n');
    
    blocks.forEach(block => {
      const lines = block.split('\n');
      if (lines.length >= 3) {
        const timeMatch = lines[1].match(/(\d{2}:\d{2}:\d{2},\d{3}) --> (\d{2}:\d{2}:\d{2},\d{3})/);
        if (timeMatch) {
          const start = timeToSeconds(timeMatch[1]);
          const end = timeToSeconds(timeMatch[2]);
          const text = lines.slice(2).join('\n');
          subtitles.push({ start, end, text });
        }
      }
    });
    
    return subtitles;
  } catch (error) {
    console.error('Error parsing SRT:', error);
    return []; // Fallback to empty array
  }
};

// Helper function to convert time string to seconds
const timeToSeconds = (timeStr: string): number => {
  try {
    const [time, ms] = timeStr.split(',');
    const [hours, minutes, seconds] = time.split(':').map(Number);
    return hours * 3600 + minutes * 60 + seconds + Number(ms) / 1000;
  } catch (error) {
    console.error('Error converting time to seconds:', timeStr, error);
    return 0; // Fallback to 0
  }
};

// Fetch subtitle content
export const fetchSubtitle = async (url: string): Promise<string> => {
  if (!url || typeof url !== 'string') {
    console.warn('Invalid subtitle URL:', url);
    return '';
  }
  
  try {
    // Fetch trực tiếp từ URL gốc WordPress, không qua proxy
    const response = await withRetry(() => axios.get(url, { timeout: 5000 }));
    return response.data || '';
  } catch (error) {
    console.error('Error fetching subtitle:', error);
    return ''; // Fallback to empty string
  }
};

// 🎯 OPTIMIZED: Fetch related posts using new caching strategy
export const fetchRelatedPosts = async (
  currentPostId: number,
  currentPostTags: number[],
  perPage = 10,
  languageCategoryId?: string
): Promise<WordPressPost[]> => {
  try {
    if (!currentPostTags || currentPostTags.length === 0) {
      console.log('🔍 No tags found for current post - returning empty array (no general posts fallback)');
      // NO fallback to general posts API - return empty array
      return [];
    }

    // ✅ NEW: Get top 6 highest ID tags for enhanced relevance
    const topTags = [...currentPostTags]
      .sort((a, b) => b - a) // Sort descending (highest ID first)
      .slice(0, 6); // Take top 6 tags

    console.log(`🔗 [API] Fetching related posts for post ${currentPostId}${languageCategoryId ? ` | Language: ${languageCategoryId}` : ''}`);
    console.log(`📋 All tags (${currentPostTags.length}): [${currentPostTags.join(', ')}]`);
    console.log(`🎯 Using top 6 highest ID tags: [${topTags.join(', ')}]`);

    // ✅ NEW: Use optimized backend endpoint that handles caching per tag
    const requestParams: any = {
        tags: topTags.join(','),
        limit: perPage
    };
    
    // Add language filter if provided
    if (languageCategoryId) {
      requestParams.languageCategoryId = languageCategoryId;
      }
    
    const response = await withRetry(() => api.get(`/related-posts/${currentPostId}`, { 
      params: requestParams
    }));
    
    if (!response.data || !Array.isArray(response.data)) {
      console.error('❌ Invalid response format from optimized related posts API');
      return [];
    }

    // Check if tags are embedded (optimized path)
    const hasEmbeddedTags = response.headers['x-tags-embedded'] === 'true';
    console.log(`[fetchRelatedPosts] ${hasEmbeddedTags ? '✅ Using embedded tags' : '⚠️ No embedded tags'} for ${response.data.length} related posts`);
    
    // Parse media URLs and handle embedded tags for each post
    const relatedPosts = response.data.map((post: WordPressPost) => {
      let tagDetails: Array<{id: number; name: string; slug: string; count: number}> = [];
      
      if (hasEmbeddedTags && post.tagDetails) {
        tagDetails = post.tagDetails;
      }
      
      return {
        ...post,
        tagDetails, // Add embedded tag details for optimization
        imageUrls: parseImageUrls(post),
        audioUrl: parseAudioUrl(post),
        subtitleUrl: parseSubtitleUrl(post)
      };
    });

    console.log(`✅ [API] Found ${relatedPosts.length} related posts using optimized endpoint`);
    console.log(`📊 [API] Related posts summary:`, relatedPosts.map(p => ({ 
      id: p.id, 
      title: p.title.rendered?.substring(0, 30) + '...', 
      tags: p.tags?.length || 0
    })));
    
    return relatedPosts;
  } catch (error) {
    console.error('❌ [API] Error fetching related posts:', error);
    
    // Fallback to old method if new endpoint fails
    console.log('🔄 [API] Falling back to old method...');
    try {
      const topTags = [...currentPostTags]
        .sort((a, b) => b - a)
        .slice(0, 6);
        
      const fallbackParams: any = {
          tags: topTags.join(','),
          per_page: perPage + 1,
          exclude: currentPostId
      };
      
      // Add language filter if provided
      if (languageCategoryId) {
        fallbackParams.languageCategoryId = languageCategoryId;
        }
      
      const response = await withRetry(() => api.get('/posts', { 
        params: fallbackParams
      }));
      
      if (response.data && Array.isArray(response.data)) {
        // Check if fallback response has embedded tags
        const hasFallbackEmbeddedTags = response.headers['x-tags-embedded'] === 'true';
        console.log(`[fetchRelatedPosts] Fallback ${hasFallbackEmbeddedTags ? '✅ has embedded tags' : '⚠️ no embedded tags'}`);
        
        const fallbackPosts = response.data
          .filter((post: WordPressPost) => post.id !== currentPostId)
          .slice(0, perPage)
          .map((post: WordPressPost) => {
            let tagDetails: Array<{id: number; name: string; slug: string; count: number}> = [];
            
            if (hasFallbackEmbeddedTags && post.tagDetails) {
              tagDetails = post.tagDetails;
            }
            
            return {
              ...post,
              tagDetails, // Add embedded tag details for optimization
              imageUrls: parseImageUrls(post),
              audioUrl: parseAudioUrl(post),
              subtitleUrl: parseSubtitleUrl(post)
            };
          });
          
        console.log(`✅ [API] Fallback: Found ${fallbackPosts.length} related posts`);
        return fallbackPosts;
      }
    } catch (fallbackError) {
      console.error('❌ [API] Fallback also failed:', fallbackError);
    }
    
    // NO FALLBACK to general posts - return empty array
    console.log('🚫 [API] No fallback to general posts API - returning empty array');
    return [];
  }
};

// Export cache key utilities for testing and monitoring
export const cacheKeyUtils = {
  generateSharedCacheKey,
  getOptimalCacheKey,
  getConservativeCacheKey,
  getCurrentCacheKey: () => getOptimalCacheKey(true),
  getConservativeCacheKeyNow: () => getConservativeCacheKey(true)
};

export default {
  fetchPosts,
  fetchPostById,
  fetchPostBySlug,
  fetchRelatedPosts,
  getProxyMediaUrl,
  getProxyImageUrl,
  getAvailableImageSizes,
  fetchSubtitle,
  parseSRT,
  fetchCategories,
  fetchTags,
  fetchFreshPosts,
  fetchPostsConservative,
  fetchPostsNoCache,
  checkForNewContent,
  mergeNewPosts,
  cacheKeyUtils,
};
